import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatErr } from "@/lib/api";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const { activeBiz, setActiveBiz, businesses, setBusinesses } = useOutletContext();
  const [keyword, setKeyword] = useState("");

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const addKw = async () => {
    if (!keyword.trim()) return;
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/keywords`, { keyword: keyword.trim() });
      const updated = { ...activeBiz, keywords: data.keywords };
      setActiveBiz(updated);
      setBusinesses(businesses.map((b) => b.id === updated.id ? updated : b));
      setKeyword("");
      toast.success("Keyword added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeKw = async (k) => {
    try {
      const { data } = await api.delete(`/businesses/${activeBiz.id}/keywords/${encodeURIComponent(k)}`);
      const updated = { ...activeBiz, keywords: data.keywords };
      setActiveBiz(updated);
      setBusinesses(businesses.map((b) => b.id === updated.id ? updated : b));
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-4xl">
      <header>
        <div className="label-tiny mb-1">Settings</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="settings-title">Settings</h1>
      </header>

      <section className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Account</h2>
        <Row k="Name" v={user?.name} />
        <Row k="Email" v={user?.email} />
        <Row k="Role" v={user?.role} />
        <Row k="Plan" v={user?.plan || "Trial"} />
      </section>

      <section className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Business · {activeBiz.name}</h2>
        <Row k="Category" v={activeBiz.category} />
        <Row k="Address" v={`${activeBiz.address || ""}, ${activeBiz.city}, ${activeBiz.state} ${activeBiz.zip_code}`} />
        <Row k="Phone" v={activeBiz.phone || "—"} />
        <Row k="Website" v={activeBiz.website || "—"} />
      </section>

      <section className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Tracked Keywords</h2>
        <div className="flex gap-2">
          <input className="input-field flex-1" placeholder="Add a keyword" value={keyword} onChange={(e)=>setKeyword(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&(e.preventDefault(),addKw())} data-testid="settings-kw-input" />
          <button className="btn-dark" onClick={addKw} data-testid="settings-kw-add"><Plus size={14} weight="bold" /> Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(activeBiz.keywords || []).map((k) => (
            <span key={k} className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 text-sm font-mono" data-testid={`settings-kw-${k}`}>
              {k}
              <button onClick={()=>removeKw(k)} className="text-zinc-500 hover:text-zinc-900"><X size={12} weight="bold" /></button>
            </span>
          ))}
          {!(activeBiz.keywords || []).length && <span className="text-zinc-400 text-sm">No keywords yet</span>}
        </div>
      </section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm border-b border-zinc-100 pb-3 last:border-b-0">
      <span className="label-tiny">{k}</span>
      <span className="col-span-2 font-mono">{v}</span>
    </div>
  );
}
